import { PromotionsProp, PromotionsItem } from '../models/sectionPromotions'
import { WebdoorList, WebdoorItem } from '../models/webdoor'

interface BannersData {
    webdoor: any[]
    carrossel: any[]
}

interface CachedBannersData {
    data: BannersData
    timestamp: number
}

const BANNERS_LOCAL_STORAGE_KEY = 'promohub-banners-data';

const CACHE_EXPIRATION_TIME = 1 * 60 * 60 * 1000;

let cachedData: CachedBannersData | null = null;
let fetchPromise: Promise<BannersData> | null = null;

const isExpired = (timestamp: number): boolean => {
    return Date.now() - timestamp > CACHE_EXPIRATION_TIME;
};

const getStoredBannersData = (): BannersData | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        const storedData = window.localStorage.getItem(BANNERS_LOCAL_STORAGE_KEY);

        if (!storedData) {
            return null;
        }

        const parsedData: CachedBannersData = JSON.parse(storedData);

        if (isExpired(parsedData.timestamp)) {
            window.localStorage.removeItem(BANNERS_LOCAL_STORAGE_KEY);
            return null;
        }

        return parsedData.data;
    } catch (error) {
        console.error('Erro ao ler cache de banners do navegador:', error);
        return null;
    }
};

const setStoredBannersData = (data: BannersData) => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const payload: CachedBannersData = {
            data,
            timestamp: Date.now()
        };

        window.localStorage.setItem(
            BANNERS_LOCAL_STORAGE_KEY,
            JSON.stringify(payload)
        );
    } catch (error) {
        console.error('Erro ao salvar cache de banners do navegador:', error);
    }
};

export const getBannersData = async (): Promise<BannersData> => {
    
    if (cachedData && !isExpired(cachedData.timestamp)) {
        return cachedData.data;
    }

    
    cachedData = null;

    
    const storedData = getStoredBannersData();

    if (storedData) {
        cachedData = {
            data: storedData,
            timestamp: Date.now()
        };

        return storedData;
    }

    
    if (fetchPromise) {
        return fetchPromise;
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const bannersApi = `${baseUrl}/api/banners-promoshell/`;

    fetchPromise = fetch(bannersApi)
        .then(response => response.json())
        .then(data => {
            cachedData = {
                data,
                timestamp: Date.now()
            };

            setStoredBannersData(data);

            fetchPromise = null;

            return data;
        })
        .catch(error => {
            fetchPromise = null;

            console.error('Erro ao buscar dados da API:', error);

            throw error;
        });

    return fetchPromise;
};

export const getPromotions = async (): Promise<PromotionsProp> => {
    try {
        const data = await getBannersData();

        const promotions: PromotionsItem[] = [];

        data.carrossel.forEach((item: any, key: number) => {
            promotions.push({
                id: key.toString(),
                title: item.title,
                description: item.description,
                images: item.image,
                actions: [
                    {
                        title: item.label || 'Saiba mais',
                        url: item.link
                    }
                ]
            });
        });

        return [
            {
                title: 'Promoções',
                description:
                    'Campanhas ativas e anteriores para você acompanhar, participar e aproveitar no momento certo.',
                promotions
            }
        ];
    } catch (error) {
        console.error('Erro ao buscar dados de promoções:', error);
        return [];
    }
};

export const getWebdoors = async (): Promise<WebdoorList> => {
    try {
        const data = await getBannersData();

        const returnData: WebdoorItem[] = [];

        data.webdoor.forEach((item: any, key: number) => {
            returnData.push({
                id: key.toString(),
                title: item.campaign_title,
                link: item.campaign_url,
                images: {
                    desktop: item.image_desktop,
                    tablet: item.image_768,
                    mobile: item.image_mobile
                }
            });
        });

        return returnData;
    } catch (error) {
        console.error('Erro ao buscar dados de webdoor:', error);
        return [];
    }
};

export interface ForYourVehicleProps {
    headline: {
        title: string
        text: string
    }
}